import { ErreurReponseIllisible } from './errors.js';
import { lectureSeuleActive } from '../horsLigne/lectureSeule.js';
import { annoncerCompteChange, signalDEpoque } from '../auth/identite.js';
import { enveloppeEstRefusLectureSeule, refusDemoLectureSeule } from '../auth/lectureSeule.js';
import { ApiError, ErreurReseau } from './errors.js';
import { modeDemonstrationActif, reponseDemo } from './demo.js';
import i18n from '../i18n.js';
import { messageValidationDetaille } from './erreursChamps.js';

/**
 * Client HTTP de la régie (`docs/dev/README.md`, `docs/dev/api.md` § 3) :
 * cookie de session (`credentials: 'include'`), en-tête anti-CSRF
 * `X-Artosera-CSRF` sur les mutations, `X-Gallery` posé depuis la galerie
 * courante. Les réponses d'erreur ont la forme `{ code, message, details }`.
 */

const ENTETE_CSRF = 'X-Artosera-CSRF';
const ENTETE_GALERIE = 'X-Gallery';
/**
 * N'importe quelle valeur non vide suffit (`docs/dev/api.md` § 3) : ce n'est
 * pas un secret, seulement la preuve qu'un appel programmatique de même
 * origine a posé l'en-tête, ce qu'un formulaire tiers ne sait pas faire.
 */
const VALEUR_CSRF = 'regie';

const BASE = '/v1';

const METHODES_MUTANTES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Ce qu'un appelant transmet EXPLICITEMENT à une requête (seconde relecture
 * du 23/09) — jamais par un état ambiant :
 *
 *  - `signal` : celui de la requête TanStack (`queryFn: ({ signal }) => …`)
 *    ou celui de l'époque capturée au lancement d'une mutation ;
 *  - `compteAttendu` : le compte sous lequel la mutation a été lancée, en
 *    `X-Artosera-Compte-Attendu` — l'API refuse (409) si la session n'est plus
 *    ce compte ;
 *  - `cleOperation` : l'identifiant d'opération d'une étape du stand, en
 *    `Idempotency-Key` (seules les routes du stand l'acceptent).
 */
export interface AppelApi {
  readonly signal?: AbortSignal;
  readonly compteAttendu?: string | null;
  readonly cleOperation?: string;
}

export interface OptionsRequete extends AppelApi {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  galerie?: string;
}

const ENTETE_COMPTE_ATTENDU = 'X-Artosera-Compte-Attendu';
const ENTETE_OPERATION = 'Idempotency-Key';

/** En-têtes de l'appel : galerie, compte attendu, opération. */
function entetesAppel(options: OptionsRequete): Record<string, string> {
  const entetes: Record<string, string> = {};
  if (options.galerie) entetes[ENTETE_GALERIE] = options.galerie;
  if (options.compteAttendu) entetes[ENTETE_COMPTE_ATTENDU] = options.compteAttendu;
  if (options.cleOperation) entetes[ENTETE_OPERATION] = options.cleOperation;
  return entetes;
}

/**
 * Le signal du `fetch` : celui transmis par l'appelant ; à défaut, celui de
 * l'époque de session COURANTE (requêtes hors TanStack : chargeurs, exports).
 */
function signalEffectif(propre: AbortSignal | undefined): AbortSignal {
  return propre ?? signalDEpoque();
}

/** Erreur d'un `fetch` : l'annulation garde sa raison, le reste est réseau. */
function erreurDeFetch(signal: AbortSignal): unknown {
  if (signal.aborted) return signal.reason ?? new DOMException('annulée', 'AbortError');
  return new ErreurReseau();
}

interface EnveloppeErreur {
  code?: string;
  message?: string;
  details?: unknown;
}

function estEnveloppeErreur(valeur: unknown): valeur is EnveloppeErreur {
  return typeof valeur === 'object' && valeur !== null;
}

/**
 * L'erreur d'une réponse non 2xx, quel que soit son format (JSON, fichier,
 * dépôt) : un `409 compte_change` sur un appel qui attendait un compte est
 * ANNONCÉ (quatrième relecture, 2) — la régie oublie ce compte, et aucun
 * fichier, aucune URL signée, n'est rendu à l'appelant.
 */
function erreurDeReponse(status: number, corps: unknown, options: AppelApi): ApiError {
  const enveloppe = estEnveloppeErreur(corps) ? corps : {};
  const raison = (enveloppe.details as { raison?: unknown } | null | undefined)?.raison;
  if (status === 409 && raison === 'compte_change' && options.compteAttendu) {
    annoncerCompteChange(options.compteAttendu);
  }
  // Module en lecture seule : un message clair, dans la langue de la régie,
  // plutôt que le texte du serveur ou une erreur générique.
  const message = enveloppeEstRefusLectureSeule(status, enveloppe.code, enveloppe.details)
    ? i18n.t('regie:module.lectureSeule.refus')
    : (enveloppe.message ?? 'Une erreur inattendue est survenue.');
  return new ApiError(
    status,
    enveloppe.code ?? 'erreur_inconnue',
    // Une validation nomme les champs fautifs : l'écran qui n'affiche que
    // `error.message` dit déjà lequel corriger, au lieu du seul message générique.
    enveloppe.code === 'validation.invalide'
      ? messageValidationDetaille(message, enveloppe.details, i18n.t)
      : message,
    enveloppe.details ?? null,
  );
}

/** Le corps JSON d'une réponse, ou `undefined` (vide, ou autre chose que du JSON). */
async function corpsJson(reponse: Response): Promise<unknown> {
  const texte = await reponse.text();
  if (texte.length === 0) return undefined;
  try {
    return JSON.parse(texte) as unknown;
  } catch {
    return undefined;
  }
}

export interface ReponseHttp<T> {
  readonly statut: number;
  readonly donnees: T;
}

/** Appelle `chemin` et conserve le statut HTTP pour les parcours asynchrones. */
export async function requeteApiAvecStatut<T>(
  chemin: string,
  options: OptionsRequete = {},
): Promise<ReponseHttp<T>> {
  if (lectureSeuleActive() && chemin !== '/auth/me' && chemin !== '/auth/logout')
    throw new ErreurReseau();
  const methode = options.method ?? 'GET';
  const cheminComplet = `${BASE}${chemin}`;

  if (modeDemonstrationActif()) {
    // La démonstration n'a pas de serveur pour refuser l'écriture d'un module
    // en lecture seule : elle rend le refus que l'API rendrait.
    const refus = refusDemoLectureSeule(
      cheminComplet,
      methode,
      i18n.t('regie:module.lectureSeule.refus'),
    );
    if (refus) throw refus;
    const simulee = reponseDemo(cheminComplet, methode, options.corps);
    if (simulee !== undefined) {
      return { statut: 200, donnees: simulee as T };
    }
  }

  const entetes = entetesAppel(options);
  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (METHODES_MUTANTES.has(methode)) entetes[ENTETE_CSRF] = VALEUR_CSRF;
  const signal = signalEffectif(options.signal);

  let reponse: Response;
  try {
    reponse = await fetch(cheminComplet, {
      method: methode,
      credentials: 'include',
      headers: entetes,
      signal,
      body: options.corps !== undefined ? JSON.stringify(options.corps) : undefined,
    });
  } catch {
    throw erreurDeFetch(signal);
  }

  if (reponse.status === 204) {
    return { statut: reponse.status, donnees: undefined as T };
  }

  let texte: string;
  try {
    texte = await reponse.text();
  } catch {
    // Un statut de refus reste connu même sans corps. Une requête annulée
    // garde toutefois sa raison (notamment une époque devenue obsolète).
    if (!signal.aborted && !reponse.ok) throw erreurDeReponse(reponse.status, undefined, options);
    throw erreurDeFetch(signal);
  }
  let donnees: unknown;
  try {
    donnees = JSON.parse(texte);
  } catch {
    /* Le statut HTTP reste prioritaire. */
  }

  if (!reponse.ok) throw erreurDeReponse(reponse.status, donnees, options);
  if (donnees === undefined) throw new ErreurReponseIllisible();

  return { statut: reponse.status, donnees: donnees as T };
}

/** Retour historique des appels JSON : le corps seul, sans changer le transport. */
export async function requeteApi<T>(chemin: string, options: OptionsRequete = {}): Promise<T> {
  return (await requeteApiAvecStatut<T>(chemin, options)).donnees;
}

export const api = {
  get: <T>(chemin: string, options: Omit<OptionsRequete, 'method' | 'corps'> = {}) =>
    requeteApi<T>(chemin, { ...options, method: 'GET' }),
  post: <T>(
    chemin: string,
    corps?: unknown,
    options: Omit<OptionsRequete, 'method' | 'corps'> = {},
  ) => requeteApi<T>(chemin, { ...options, method: 'POST', corps }),
  put: <T>(
    chemin: string,
    corps?: unknown,
    options: Omit<OptionsRequete, 'method' | 'corps'> = {},
  ) => requeteApi<T>(chemin, { ...options, method: 'PUT', corps }),
  patch: <T>(
    chemin: string,
    corps?: unknown,
    options: Omit<OptionsRequete, 'method' | 'corps'> = {},
  ) => requeteApi<T>(chemin, { ...options, method: 'PATCH', corps }),
  delete: <T>(chemin: string, options: Omit<OptionsRequete, 'method' | 'corps'> = {}) =>
    requeteApi<T>(chemin, { ...options, method: 'DELETE' }),
};

/**
 * Dépose un fichier (`multipart/form-data`) : médias, import d'œuvres. Ni
 * `requeteApi` (qui sérialise toujours en JSON) ni `fetch` nu (qui oublierait
 * le cookie de session et l'en-tête anti-CSRF) ne conviennent seuls.
 */
export async function requeteMultipartAvecStatut<T>(
  chemin: string,
  donnees: FormData,
  options: Omit<OptionsRequete, 'method' | 'corps'> = {},
): Promise<ReponseHttp<T>> {
  if (lectureSeuleActive()) throw new ErreurReseau();
  const entetes = { ...entetesAppel(options), [ENTETE_CSRF]: VALEUR_CSRF };
  const signal = signalEffectif(options.signal);

  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}${chemin}`, {
      method: 'POST',
      credentials: 'include',
      headers: entetes,
      body: donnees,
      signal,
    });
  } catch {
    throw erreurDeFetch(signal);
  }

  const texte = await reponse.text();
  const corps: unknown = texte.length > 0 ? JSON.parse(texte) : undefined;

  if (!reponse.ok) throw erreurDeReponse(reponse.status, corps, options);

  return { statut: reponse.status, donnees: corps as T };
}

/** Retour historique des uploads, avec le même transport et les mêmes gardes. */
export async function requeteMultipart<T>(
  chemin: string,
  donnees: FormData,
  options: Omit<OptionsRequete, 'method' | 'corps'> = {},
): Promise<T> {
  return (await requeteMultipartAvecStatut<T>(chemin, donnees, options)).donnees;
}

export interface FichierTelecharge {
  readonly blob: Blob;
  readonly nomFichier: string | null;
}

/** L’URL signée du stockage s’utilise sans transmettre les identifiants de session. */
export async function telechargerUrlSignee(url: string, signal: AbortSignal): Promise<Blob> {
  let reponse: Response;
  try {
    reponse = await fetch(url, { credentials: 'omit', signal });
  } catch {
    throw erreurDeFetch(signal);
  }
  if (!reponse.ok) throw erreurDeReponse(reponse.status, undefined, {});
  return reponse.blob();
}

/** Télécharge un fichier (export CSV/XLSX) : la réponse n'est pas du JSON. */
export async function requeteTelechargement(
  chemin: string,
  options: OptionsRequete = {},
): Promise<FichierTelecharge> {
  if (lectureSeuleActive()) throw new ErreurReseau();
  const entetes = entetesAppel(options);
  const methode = options.method ?? 'GET';
  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (METHODES_MUTANTES.has(methode)) entetes[ENTETE_CSRF] = VALEUR_CSRF;
  const signal = signalEffectif(options.signal);

  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}${chemin}`, {
      method: methode,
      body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
      credentials: 'include',
      headers: entetes,
      signal,
    });
  } catch {
    throw erreurDeFetch(signal);
  }

  if (!reponse.ok) throw erreurDeReponse(reponse.status, await corpsJson(reponse), options);

  const disposition = reponse.headers.get('Content-Disposition') ?? '';
  const correspondance = /filename="([^"]+)"/.exec(disposition);
  return { blob: await reponse.blob(), nomFichier: correspondance?.[1] ?? null };
}

/**
 * Enregistre un fichier téléchargé par `requeteTelechargement` : un lien
 * local (`blob:`), jamais une URL de l'API — le fichier a déjà passé le
 * contrôle du compte attendu.
 */
export function enregistrerFichier(fichier: FichierTelecharge, nomParDefaut: string): void {
  enregistrerBlob(fichier.blob, fichier.nomFichier ?? nomParDefaut);
}

/** Enregistre un contenu produit par la régie (export JSON d'une réponse). */
export function enregistrerBlob(blob: Blob, nomFichier: string): void {
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.append(lien);
  lien.click();
  lien.remove();
  // Révoqué après coup : le navigateur a le temps de lire le lien local.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1_000);
}

/**
 * Chemin du lien NOMINATIF d'une archive (`GET /v1/exports/:jeton`, ARTO-54).
 *
 * Seule exception au passage par `requeteApi` pour un téléchargement :
 * l'archive peut peser plusieurs gigaoctets, le NAVIGATEUR la télécharge en
 * flux (lien `download`), sans la faire transiter par la mémoire de la page —
 * donc sans en-tête de compte attendu. Le compte n'en est pas moins vérifié :
 * le jeton est rattaché à UN compte, et l'API refuse (404) toute session qui
 * n'est pas la sienne. Le chemin est composé ICI, comme tout chemin de l'API.
 */
export function cheminLienArchive(jeton: string): string {
  return `${BASE}/exports/${encodeURIComponent(jeton)}`;
}

// ── Lien de dépôt public (ARTO-129) ───────────────────────────────────────

/** Le secret d'un envoi par lien, rendu à son ouverture, exigé par chaque geste suivant. */
const ENTETE_SECRET_ENVOI = 'X-Artosera-Envoi';

/**
 * Une requête de la page publique du lien de dépôt (`/depot/:jeton`) : SANS
 * cookie, sans galerie ni compte attendu. La page n'a pas de session — le
 * jeton du chemin est sa seule autorisation — et aucune de ces routes ne rend
 * une donnée : elles servent à envoyer.
 */
export async function requeteDepotPublic<T>(
  jeton: string,
  suite: string,
  options: {
    method?: 'GET' | 'POST';
    corps?: unknown;
    signal?: AbortSignal;
    /** Le secret de l'envoi, rendu à son ouverture et exigé ensuite. */
    secret?: string;
  } = {},
): Promise<ReponseHttp<T>> {
  const signal = options.signal ?? new AbortController().signal;
  const entetes: Record<string, string> = {};
  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (options.secret !== undefined) entetes[ENTETE_SECRET_ENVOI] = options.secret;
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}/depot/${encodeURIComponent(jeton)}${suite}`, {
      method: options.method ?? 'GET',
      credentials: 'omit',
      headers: entetes,
      body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
      signal,
    });
  } catch {
    throw erreurDeFetch(signal);
  }
  const donnees = await corpsJson(reponse);
  if (!reponse.ok) throw erreurDeReponse(reponse.status, donnees, {});
  return { statut: reponse.status, donnees: donnees as T };
}

/**
 * Un morceau d'un dépôt public, par `XMLHttpRequest` — seul moyen de suivre la
 * progression d'un envoi. À l'URL SIGNÉE du stockage (mode direct), ou à
 * l'API du lien (repli). Jamais de cookie. Rend le statut HTTP ; 0 : réseau.
 */
export function envoyerMorceauDepotPublic(
  cible:
    | { readonly url: string }
    | {
        readonly jeton: string;
        readonly fichierId: string;
        readonly numero: number;
        readonly secret: string;
      },
  corps: Blob,
  progres: (octets: number) => void,
  signal: AbortSignal,
): Promise<number> {
  const url =
    'url' in cible
      ? cible.url
      : `${BASE}/depot/${encodeURIComponent(cible.jeton)}/fichiers/${encodeURIComponent(
          cible.fichierId,
        )}/morceaux/${String(cible.numero)}`;
  return new Promise((resoudre) => {
    const envoi = new XMLHttpRequest();
    envoi.open('PUT', url);
    if (!('url' in cible)) {
      envoi.setRequestHeader('Content-Type', 'application/octet-stream');
      envoi.setRequestHeader(ENTETE_SECRET_ENVOI, cible.secret);
    }
    envoi.upload.onprogress = (evenement) => progres(evenement.loaded);
    envoi.onload = () => resoudre(envoi.status);
    envoi.onerror = () => resoudre(0);
    envoi.onabort = () => resoudre(-1);
    signal.addEventListener('abort', () => envoi.abort(), { once: true });
    envoi.send(corps);
  });
}

/**
 * Ouvre une URL SIGNÉE rendue par l'API (PDF, pièce du coffre) : un jeton de
 * quelques minutes sur le stockage, déjà délivré au compte attendu. Seul
 * `window.open` de la régie (quatrième relecture, 2) : une URL de l'API
 * elle-même partirait sans compte attendu ni signal, elle est refusée.
 */
export function ouvrirUrlSignee(url: string): void {
  const cible = new URL(url, window.location.origin);
  if (cible.origin === window.location.origin && cible.pathname.startsWith(`${BASE}/`)) {
    throw new Error('Une URL de l’API ne s’ouvre pas hors du client : passez par requeteApi.');
  }
  window.open(cible.href, '_blank', 'noopener');
}

/**
 * Quitte la régie pour la page de paiement rendue par l'API (ou son retour
 * simulé). Comme `ouvrirUrlSignee`, une URL de l'API elle-même est refusée.
 */
export function naviguerVersPaiement(url: string): void {
  const cible = new URL(url, window.location.origin);
  if (cible.origin === window.location.origin && cible.pathname.startsWith(`${BASE}/`)) {
    throw new Error('Une URL de l’API ne s’ouvre pas hors du client : passez par requeteApi.');
  }
  window.location.assign(cible.href);
}

/** Navigations publiques SSO : toutes les adresses de l’API restent dans ce client. */
export function urlDebutSso(slug: string, retour: string): string {
  return `${BASE}/auth/sso/${encodeURIComponent(slug)}/debut?retour=${encodeURIComponent(retour)}`;
}
export function naviguerVers(url: string): void {
  window.location.assign(url);
}
export function urlRappelSso(): string {
  return `${window.location.origin}${BASE}/auth/sso/rappel`;
}
